/**
 * AdvancedFilterToolbar.js
 *
 * LGX-653: generic "Filters" toggle button + active-filter pills, adapted from Shipment's
 * AdvancedFilterToolbar/ActiveFilterPills (TabContent/shipmentShared.js) to take a `ruleFields`
 * prop instead of a hardcoded field catalog.
 */
import React from "react";
import { SlidersHorizontal, RotateCcw, X } from "lucide-react";
import { pruneRuleNodes, describeNode, removeRuleNode } from "./filterTree";

const ActiveFilterPills = ({ ruleFields, filters, onRemove }) => {
  const pruned = pruneRuleNodes(ruleFields, filters?.rules || []);
  if (!pruned.length) return null;

  return (
    <div className="flex flex-wrap gap-1.5 px-3">
      {pruned.map((node) => (
        <span
          key={node.id}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800"
        >
          {describeNode(ruleFields, node)}
          <button
            onClick={() => onRemove(node.id)}
            className="ml-0.5 hover:text-blue-900 dark:hover:text-blue-100 transition-colors"
          >
            <X size={11} />
          </button>
        </span>
      ))}
    </div>
  );
};

/**
 * @param {Array}  ruleFields
 * @param {object} filters       - { rules, matchType }
 * @param {number} activeCount
 * @param {func}   onOpenModal
 * @param {func}   onClearAll
 * @param {func}   onChangeFilters - (nextFilters) => void, used by the pill remove buttons
 * @param {node}   trailing        - extra content (e.g. SavedFiltersControl) rendered after the buttons
 */
export const AdvancedFilterToolbar = ({
  ruleFields,
  filters,
  activeCount,
  onOpenModal,
  onClearAll,
  onChangeFilters,
  trailing = null,
}) => {
  const handleRemovePill = (nodeId) => {
    onChangeFilters({ ...filters, rules: removeRuleNode(filters?.rules || [], nodeId) });
  };

  return (
    <>
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-200 dark:border-acs-dark-border flex-shrink-0 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            onClick={onOpenModal}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border transition-all duration-150 ${
              activeCount > 0
                ? "bg-blue-600 border-blue-600 text-white hover:bg-blue-700"
                : "bg-white dark:bg-acs-dark-surface border-gray-300 dark:border-acs-dark-border text-gray-700 dark:text-acs-dark-text hover:border-blue-400 dark:hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400"
            }`}
          >
            <SlidersHorizontal size={15} />
            Advanced Filters
            {activeCount > 0 && (
              <span className="ml-0.5 bg-white/25 text-white text-xs font-semibold rounded-full px-1.5 py-0.5 leading-none">
                {activeCount}
              </span>
            )}
          </button>

          {activeCount > 0 && (
            <button
              onClick={onClearAll}
              className="text-xs text-gray-400 hover:text-red-500 dark:hover:text-red-400 flex items-center gap-1 transition-colors"
            >
              <RotateCcw size={12} /> Clear all
            </button>
          )}
        </div>
        {trailing}
      </div>

      {activeCount > 0 && (
        <div className="pt-2">
          <ActiveFilterPills ruleFields={ruleFields} filters={filters} onRemove={handleRemovePill} />
        </div>
      )}
    </>
  );
};

export default AdvancedFilterToolbar;
